import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

interface Section {
  id: string;
  label: string;
}

/**
 * The sections of the page it is placed on, as a list of links.
 *
 * <p><b>Why it reads the DOM rather than being handed a list.</b> Most sections of an event page
 * are conditional, and three of them decide for themselves: {@code TicketList} returns null on an
 * empty array, {@code EventLocationMap} returns null without coordinates, and the closing note has
 * two forms. A caller passing a list would have to restate every one of those conditions, and
 * would go wrong the first time one changed — offering a Tickets link on a page with no tickets.
 * Reading what actually rendered cannot drift from it.
 *
 * <p>So the contract is the markup: anything carrying an {@code id} and a {@code data-section}
 * label appears here, in document order, and nothing needs to be told about it.
 */
export function SectionNav({
  contentRef,
  watch,
}: {
  contentRef: RefObject<HTMLElement | null>;
  watch: unknown;
}) {
  const [sections, setSections] = useState<Section[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // `watch` is the event id. The scan has to run again for a different event, whose sections are
  // not the same ones — but not on every render, which would restart the observer below endlessly.
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const found = Array.from(root.querySelectorAll<HTMLElement>('[data-section][id]')).map((el) => ({
      id: el.id,
      label: el.dataset.section ?? el.id,
    }));
    setSections(found);
    setActiveId(found[0]?.id ?? null);
  }, [contentRef, watch]);

  /*
   * Which section the reader is in.
   *
   * The band is thin and near the top of the viewport: a section becomes current when its heading
   * arrives where a reader is looking, not when any part of it is on screen — by that measure
   * three sections are current at once on a tall display.
   *
   * When nothing is in the band the previous answer stands. Passing through a short section
   * shouldn't blank the highlight, and at the foot of a long page no heading is near the top.
   */
  useEffect(() => {
    if (sections.length === 0) return;

    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        const current = sections.find((section) => visible.has(section.id));
        if (current) setActiveId(current.id);
      },
      { rootMargin: '-72px 0px -75% 0px', threshold: 0 },
    );

    for (const { id } of sections) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [sections]);

  // On a phone the list scrolls sideways, so the current chip can sit off the edge — which turns
  // the one thing it exists to tell you into the one thing you can't see. This moves the list
  // alone, never the page: scrollIntoView would drag the document along with it.
  useEffect(() => {
    const list = listRef.current;
    if (!list || !activeId) return;
    const chip = list.querySelector<HTMLElement>(`[data-chip="${activeId}"]`);
    if (!chip) return;
    const left = chip.offsetLeft - list.clientWidth / 2 + chip.clientWidth / 2;
    list.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  }, [activeId]);

  // One section isn't a contents list, it's a label for the whole page.
  if (sections.length < 2) return null;

  return (
    /*
     * Two elements, because the rail needs both at once on a wide screen: an outer box as tall as
     * the article, and something inside it that sticks. Sticky travels only within its own
     * container, so a single element would either be the full height and not stick, or stick and
     * have nowhere to travel.
     */
    <nav className="section-nav" aria-label="Sections on this page">
      <div className="section-nav__inner">
        <p className="section-nav__title">On this page</p>
        <ul className="section-nav__list" ref={listRef}>
          {sections.map((section) => (
            <li key={section.id}>
              {/*
                A real anchor rather than a button with a scroll handler: it survives a failed
                hydration, opens in a new tab on a middle click, and can be copied as a link to a
                single section — none of which a click handler gives you. The offset for the sticky
                chip row is scroll-margin-top and the easing is scroll-behavior, both in CSS, so
                neither needs code that would have to remember prefers-reduced-motion for itself.
              */}
              <a
                className={
                  section.id === activeId
                    ? 'section-nav__link section-nav__link--current'
                    : 'section-nav__link'
                }
                href={`#${section.id}`}
                data-chip={section.id}
                aria-current={section.id === activeId ? 'true' : undefined}
              >
                {section.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
